import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8005';

export default function ConversationalPortal() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [portal, setPortal] = useState(null);
  const [error, setError] = useState(null);
  
  // Conversation state
  const [agentId, setAgentId] = useState(null);
  const [wsUrl, setWsUrl] = useState(null);
  const [apiKey, setApiKey] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isTalking, setIsTalking] = useState(false);
  const [conversationStarted, setConversationStarted] = useState(false);
  const [status, setStatus] = useState('Click to start conversation');
  
  const wsRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioContextRef = useRef(null);

  useEffect(() => {
    loadPortal();
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (mediaRecorderRef.current) mediaRecorderRef.current.stop();
    };
  }, [token]);

  const loadPortal = async () => {
    try {
      const response = await axios.get(`${API_URL}/portal/${token}`);
      setPortal(response.data.portal);
    } catch (error) {
      console.error('Failed to load portal:', error);
      setError('Portal not found or invalid link');
    } finally {
      setLoading(false);
    }
  };

  const startConversation = async () => {
    try {
      console.log('Starting conversation for token:', token);
      setStatus('Connecting...');
      
      // Get conversation agent details
      console.log('Calling:', `${API_URL}/conversation/start/${token}`);
      const response = await axios.get(`${API_URL}/conversation/start/${token}`);
      console.log('Response:', response.data);
      const { agent_id, websocket_url, api_key, creator_name } = response.data;
      
      setAgentId(agent_id);
      setWsUrl(websocket_url);
      setApiKey(api_key);
      setPortal(prev => ({ ...prev, creator_name }));
      
      // Connect to ElevenLabs WebSocket
      const ws = new WebSocket(websocket_url);
      ws.binaryType = 'arraybuffer'; // Accept binary audio responses
      wsRef.current = ws;
      
      ws.onopen = () => {
        console.log('WebSocket connected');
        
        // Send initial message to start conversation
        const initMessage = {
          type: 'conversation_initiation_client_data',
          conversation_initiation_client_data: {
            conversation_config_override: {}
          }
        };
        
        ws.send(JSON.stringify(initMessage));
        console.log('Sent init message');
        
        setIsConnected(true);
        setConversationStarted(true);
        setStatus('Connected! Hold button to talk');
      };
      
      ws.onmessage = async (event) => {
        // ElevenLabs sends both JSON messages and audio chunks
        if (typeof event.data === 'string') {
          // JSON message (status, transcript, etc.)
          try {
            const message = JSON.parse(event.data);
            console.log('Received message:', message);
            
            if (message.type === 'conversation_initiation_metadata') {
              console.log('Conversation started');
            } else if (message.type === 'audio') {
              // Audio data in base64 - ElevenLabs sends PCM 16-bit audio at 24kHz
              const base64Audio = message.audio_event.audio_base_64;
              const binaryString = atob(base64Audio);
              const bytes = new Uint8Array(binaryString.length);
              for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
              }
              
              // Initialize AudioContext if needed
              if (!audioContextRef.current) {
                audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
              }
              
              // Convert PCM bytes to Float32Array for Web Audio API
              const pcmData = new Int16Array(bytes.buffer);
              const sampleRate = 24000; // ElevenLabs uses 24kHz
              const audioBuffer = audioContextRef.current.createBuffer(1, pcmData.length, sampleRate);
              const channelData = audioBuffer.getChannelData(0);
              
              // Convert Int16 to Float32 (-1.0 to 1.0)
              for (let i = 0; i < pcmData.length; i++) {
                channelData[i] = pcmData[i] / 32768.0;
              }
              
              // Play the audio
              const source = audioContextRef.current.createBufferSource();
              source.buffer = audioBuffer;
              source.connect(audioContextRef.current.destination);
              source.start(0);
              source.onended = () => {
                setStatus('Ready to talk');
              };
            } else if (message.type === 'user_transcript' || message.type === 'agent_response') {
              console.log('Transcript:', message);
            } else if (message.type === 'ping') {
              // Respond to ping to keep connection alive
              const pongMessage = {
                type: 'pong',
                event_id: message.ping_event.event_id
              };
              wsRef.current.send(JSON.stringify(pongMessage));
            } else {
              console.log('Other message type:', message.type);
            }
          } catch (e) {
            console.error('Failed to parse message:', e);
          }
        } else {
          // Binary audio data
          await playAudio(event.data);
        }
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setStatus('Connection error. Please refresh.');
      };
      
      ws.onclose = (event) => {
        console.log('WebSocket closed', event.code, event.reason);
        setIsConnected(false);
        setStatus('Disconnected');
        
        // Stop recording if active
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
          mediaRecorderRef.current.stop();
          mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
        }
      };
      
    } catch (error) {
      console.error('Failed to start conversation:', error);
      console.error('Error details:', error.response?.data || error.message);
      setStatus(`Error: ${error.response?.data?.detail || error.message}`);
      alert(`Failed to start conversation: ${error.response?.data?.detail || error.message}`);
    }
  };

  const startTalking = async () => {
    if (!isConnected) {
      console.log('Not connected, cannot start talking');
      return;
    }
    
    try {
      setIsTalking(true);
      setStatus('Listening...');
      
      // Get microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000
        }
      });
      
      // Use Web Audio API to capture PCM audio
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 16000 });
      }
      
      const source = audioContextRef.current.createMediaStreamSource(stream);
      const processor = audioContextRef.current.createScriptProcessor(4096, 1, 1);
      
      let isRecording = true;
      
      processor.onaudioprocess = (e) => {
        if (wsRef.current?.readyState === WebSocket.OPEN && isRecording) {
          const inputData = e.inputBuffer.getChannelData(0);
          
          // Convert Float32 to Int16 PCM
          const pcmData = new Int16Array(inputData.length);
          for (let i = 0; i < inputData.length; i++) {
            const s = Math.max(-1, Math.min(1, inputData[i]));
            pcmData[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
          }
          
          // Convert to base64
          const uint8Array = new Uint8Array(pcmData.buffer);
          const base64 = btoa(String.fromCharCode.apply(null, uint8Array));
          
          const audioMessage = {
            user_audio_chunk: base64
          };
          
          console.log('Sending PCM audio chunk');
          wsRef.current.send(JSON.stringify(audioMessage));
        }
      };
      
      // Store for cleanup
      mediaRecorderRef.current = { stream, source, processor, stopRecording: () => { isRecording = false; } };
      
      source.connect(processor);
      processor.connect(audioContextRef.current.destination);
      
    } catch (error) {
      console.error('Failed to access microphone:', error);
      setStatus('Microphone access denied');
      setIsTalking(false);
    }
  };

  const stopTalking = () => {
    if (mediaRecorderRef.current) {
      // Clean up Web Audio API nodes
      if (mediaRecorderRef.current.processor) {
        mediaRecorderRef.current.processor.disconnect();
        mediaRecorderRef.current.source.disconnect();
      }
      if (mediaRecorderRef.current.stream) {
        mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      }
      // Old MediaRecorder cleanup
      if (mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
    }
    setIsTalking(false);
    setStatus('Processing...');
  };

  const playAudio = async (audioData) => {
    try {
      // Initialize AudioContext if needed
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      // audioData is already an ArrayBuffer from WebSocket
      let arrayBuffer;
      if (audioData instanceof ArrayBuffer) {
        arrayBuffer = audioData;
      } else if (audioData instanceof Blob) {
        arrayBuffer = await audioData.arrayBuffer();
      } else {
        console.error('Unknown audio data type:', typeof audioData);
        return;
      }
      
      // Decode and play
      const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);
      const source = audioContextRef.current.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(audioContextRef.current.destination);
      source.start(0);
      
      source.onended = () => {
        setStatus('Ready to talk');
      };
      
    } catch (error) {
      console.error('Failed to play audio:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-white mb-4"></div>
          <p className="text-white text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-slate-900 flex items-center justify-center">
        <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-12 text-center max-w-md">
          <p className="text-2xl text-white mb-4">❌</p>
          <h2 className="text-2xl font-bold text-white mb-2">Portal Not Found</h2>
          <p className="text-purple-200">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-slate-900 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-block mb-6">
            <div className="w-32 h-32 rounded-full bg-gradient-to-br from-purple-400 to-pink-600 flex items-center justify-center shadow-2xl">
              <span className="text-6xl">👤</span>
            </div>
          </div>
          <h1 className="text-5xl font-bold text-white mb-3">
            Talk with {portal?.creator_name || 'Your Loved One'}
          </h1>
          <p className="text-xl text-purple-200 mb-2">
            For {portal?.recipient_name}
          </p>
          <p className="text-purple-300 text-sm">
            "I'm here whenever you need me. Just press and talk."
          </p>
        </div>

        {/* Conversation Interface */}
        <div className="bg-black/40 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/10 p-8 md:p-12">
          
          {!conversationStarted ? (
            /* Start Button */
            <div className="text-center">
              <button
                onClick={startConversation}
                className="group relative inline-flex items-center justify-center px-16 py-8 text-2xl font-bold rounded-full shadow-2xl text-white bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 transition-all transform hover:scale-105"
              >
                <div className="absolute inset-0 rounded-full bg-green-400 blur-xl opacity-50 group-hover:opacity-75 transition-opacity"></div>
                <span className="relative">🎙️ Start Conversation</span>
              </button>
              <p className="text-purple-300 text-sm mt-6">
                Click to connect and start talking
              </p>
            </div>
          ) : (
            /* Talk Interface */
            <div className="text-center space-y-8">
              
              {/* Status */}
              <div className="bg-white/5 rounded-2xl p-6">
                <p className="text-purple-200 text-lg mb-2">Status</p>
                <p className="text-white text-xl font-semibold">{status}</p>
              </div>

              {/* Talk Button */}
              <div className="py-8">
                <button
                  onMouseDown={startTalking}
                  onMouseUp={stopTalking}
                  onTouchStart={startTalking}
                  onTouchEnd={stopTalking}
                  disabled={!isConnected}
                  className={`group relative inline-flex items-center justify-center w-48 h-48 rounded-full shadow-2xl text-white transition-all transform ${
                    isTalking 
                      ? 'bg-gradient-to-r from-red-500 to-pink-600 scale-110' 
                      : 'bg-gradient-to-r from-blue-500 to-indigo-600 hover:scale-105'
                  } ${!isConnected ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className={`absolute inset-0 rounded-full blur-xl opacity-50 transition-opacity ${
                    isTalking ? 'bg-red-400 opacity-75 animate-pulse' : 'bg-blue-400'
                  }`}></div>
                  <div className="relative text-center">
                    <div className="text-6xl mb-2">🎤</div>
                    <div className="text-lg font-bold">
                      {isTalking ? 'Listening...' : 'Hold to Talk'}
                    </div>
                  </div>
                </button>
              </div>

              {/* Instructions */}
              <div className="bg-purple-900/30 border border-purple-500/30 rounded-2xl p-6">
                <p className="text-purple-200 text-sm">
                  💡 <strong>How to use:</strong> Press and hold the button, speak your message, then release. 
                  You'll hear the response in their voice.
                </p>
              </div>

              {/* End Conversation */}
              <button
                onClick={() => {
                  if (wsRef.current) wsRef.current.close();
                  setConversationStarted(false);
                  setIsConnected(false);
                }}
                className="text-purple-300 hover:text-white text-sm underline"
              >
                End Conversation
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-12 text-purple-300 text-sm">
          <p>This is your personal conversation portal</p>
          <p className="mt-1">Bookmark this page to talk anytime</p>
        </div>
      </div>
    </div>
  );
}
